import { useRef, useCallback, useEffect } from "react";
import api from "api/axios";
import { handleAxiosAndCaughtError } from "utils";
import { Message, PaginatedMessages, Reference } from "../types";
import { NavigateFunction } from "react-router-dom";

interface UseChatHandlersProps {
  inputMessage: string;
  setInputMessage: (msg: string) => void;
  isLoading: boolean;
  setIsLoading: (b: boolean) => void;
  isSending: boolean;
  setIsSending: (b: boolean) => void;
  hasMoreOlder: boolean;
  setHasMoreOlder: (b: boolean) => void;
  hasMoreNewer: boolean;
  setHasMoreNewer: (b: boolean) => void;
  isLoadingOlder: boolean;
  setIsLoadingOlder: (b: boolean) => void;
  isLoadingNewer: boolean;
  setIsLoadingNewer: (b: boolean) => void;
  loadError: string | null;
  setLoadError: (msg: string | null) => void;
  olderError: string | null;
  setOlderError: (msg: string | null) => void;
  newerError: string | null;
  setNewerError: (msg: string | null) => void;
  isAtBottom: boolean;
  setIsAtBottom: (b: boolean) => void;
  messagesContainerRef: React.RefObject<HTMLDivElement>;
  topSentinelRef: React.RefObject<HTMLDivElement>;
  bottomSentinelRef: React.RefObject<HTMLDivElement>;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  messages: Message[];
  fetchConversations?: () => void;
  conversationId?: string;
  agentId: string;
  navigate: NavigateFunction;
}

export function useChatHandlers({
  inputMessage,
  setInputMessage,
  setIsLoading,
  isSending,
  setIsSending,
  hasMoreOlder,
  setHasMoreOlder,
  hasMoreNewer,
  setHasMoreNewer,
  isLoadingOlder,
  setIsLoadingOlder,
  isLoadingNewer,
  setIsLoadingNewer,
  setLoadError,
  olderError,
  setOlderError,
  newerError,
  setNewerError,
  setIsAtBottom,
  messagesContainerRef,
  setMessages,
  messages,
  fetchConversations,
  conversationId,
  agentId,
  navigate,
}: UseChatHandlersProps) {
  const abortControllerRef = useRef<AbortController | null>(null);

  const cleanup = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const createAbortController = useCallback(() => {
    cleanup();
    abortControllerRef.current = new AbortController();
    return abortControllerRef.current;
  }, [cleanup]);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const createNewConversation = async (messageContent: string) => {
    try {
      const abortController = createAbortController();
      const response = await api.post(
        "/conversations",
        {
          agent_id: agentId,
          name: messageContent.split(" ").slice(0, 10).join(" "),
        },
        {
          signal: abortController.signal,
        }
      );
      return response.data.id;
    } catch (error: any) {
      if (error.name === "CanceledError") {
        console.log("Request aborted");
        return;
      }
      handleAxiosAndCaughtError(error);
    }
  };

  const addMessageToConversation = async (
    conversationId: string,
    messageContent: string
  ) => {
    try {
      const abortController = createAbortController();
      const response = await api.post<{
        id: string;
        reply: string;
        references: Reference[];
      }>(
        `/conversations/${conversationId}/messages`,
        {
          content: messageContent,
        },
        {
          signal: abortController.signal,
        }
      );

      if (fetchConversations) fetchConversations();

      return response.data;
    } catch (error: any) {
      if (error.name === "CanceledError") {
        console.log("Request aborted");
        return;
      }
      handleAxiosAndCaughtError(error);
    }
  };

  const addOptimisticMessage = (content: string) => {
    const tempUserMessage: Message = {
      id: `temp-${Date.now()}`,
      content,
      role: "USER",
      created_at: new Date().toISOString(),
    };
    setMessages((prev: Message[]) => [...prev, tempUserMessage]);
    scrollToBottom();
    return tempUserMessage;
  };

  const updateMessagesWithResponse = (
    tempUserMessage: Message,
    userMessageId: string,
    aiResponse: string,
    aiRefernces: Reference[]
  ) => {
    setMessages((prev: Message[]) => {
      const filtered = prev.filter((msg) => msg.id !== tempUserMessage.id);

      const userMessage: Message = {
        id: userMessageId,
        content: tempUserMessage.content,
        role: "USER",
        created_at: new Date().toISOString(),
      };

      const aiMessage: Message = {
        id: `ai-${Date.now()}`,
        content: aiResponse,
        role: "SYSTEM",
        created_at: new Date().toISOString(),
        references: aiRefernces,
      };

      return [...filtered, userMessage, aiMessage];
    });
  };

  const loadInitialMessages = useCallback(async () => {
    if (!conversationId) return;

    setIsLoading(true);
    setLoadError(null);
    try {
      const abortController = createAbortController();
      const response = await api.get<PaginatedMessages>(
        `/conversations/${conversationId}/messages`,
        {
          params: { take: 20, skip: 0 },
          signal: abortController.signal,
        }
      );

      const data = response.data;
      setMessages(data.items.reverse());
      setHasMoreOlder(data.items.length === 20);
      setHasMoreNewer(false);

      setTimeout(() => scrollToBottom(), 100);
    } catch (error: any) {
      if (error.name === "CanceledError") {
        console.log("Request aborted");
        return;
      }
      handleAxiosAndCaughtError(error);
    } finally {
      setIsLoading(false);
    }
  }, [conversationId, createAbortController, setMessages]);

  const loadOlderMessages = useCallback(async () => {
    if (
      !conversationId ||
      !hasMoreOlder ||
      isLoadingOlder ||
      messages.length === 0
    )
      return;

    setIsLoadingOlder(true);
    setOlderError(null);
    try {
      const abortController = createAbortController();
      const oldestMessage = messages[0];
      const response = await api.get<PaginatedMessages>(
        `/conversations/${conversationId}/messages`,
        {
          params: {
            take: 20,
            skip: 0,
            before_message_id: oldestMessage.id,
          },
          signal: abortController.signal,
        }
      );

      const data = response.data;
      const newMessages = data.items.reverse();

      if (newMessages.length > 0) {
        const scrollHeight = messagesContainerRef.current?.scrollHeight || 0;
        setMessages((prev: Message[]) => [...newMessages, ...prev]);

        setTimeout(() => {
          if (messagesContainerRef.current) {
            const newScrollHeight = messagesContainerRef.current.scrollHeight;
            messagesContainerRef.current.scrollTop =
              newScrollHeight - scrollHeight;
          }
        }, 0);
      }

      setHasMoreOlder(newMessages.length === 20);
      setTimeout(() => scrollToBottom(), 100);
    } catch (error: any) {
      if (error.name === "CanceledError") {
        console.log("Request aborted");
        return;
      }
      setOlderError("Failed to load older messages.");
      handleAxiosAndCaughtError(error);
    } finally {
      setIsLoadingOlder(false);
    }
  }, [
    conversationId,
    hasMoreOlder,
    isLoadingOlder,
    messages,
    createAbortController,
    setMessages,
  ]);

  const loadNewerMessages = useCallback(async () => {
    if (
      !conversationId ||
      !hasMoreNewer ||
      isLoadingNewer ||
      messages.length === 0
    )
      return;

    setIsLoadingNewer(true);
    setNewerError(null);
    try {
      const abortController = createAbortController();
      const newestMessage = messages[messages.length - 1];
      const response = await api.get<PaginatedMessages>(
        `/conversations/${conversationId}/messages`,
        {
          params: {
            take: 20,
            skip: 0,
            after_message_id: newestMessage.id,
          },
          signal: abortController.signal,
        }
      );

      const data = response.data;
      const newMessages = data.items.reverse();

      if (newMessages.length > 0) {
        setMessages((prev: Message[]) => [...prev, ...newMessages]);
      }

      setHasMoreNewer(newMessages.length === 20);
    } catch (error: any) {
      if (error.name === "CanceledError") {
        console.log("Request aborted");
        return;
      }
      setNewerError("Failed to load newer messages.");
      handleAxiosAndCaughtError(error);
    } finally {
      setIsLoadingNewer(false);
    }
  }, [
    conversationId,
    hasMoreNewer,
    isLoadingNewer,
    messages,
    createAbortController,
    setMessages,
  ]);

  const sendMessage = async () => {
    if (!inputMessage.trim() || isSending) return;

    const messageContent = inputMessage.trim();
    setInputMessage("");
    setIsSending(true);

    const tempUserMessage = addOptimisticMessage(messageContent);

    try {
      let newConversationId = conversationId;
      let response;

      if (!conversationId) {
        try {
          newConversationId = await createNewConversation(messageContent);
          if (!newConversationId) return; // Request was aborted
          response = await addMessageToConversation(
            newConversationId,
            messageContent
          );
          if (!response) return;
        } catch (error) {
          setMessages((prev: Message[]) =>
            prev.filter((msg) => msg.id !== tempUserMessage.id)
          );
          setIsSending(false);
          handleAxiosAndCaughtError(error);
        } finally {
          navigate(`/dashboard/chat/${agentId}/${newConversationId}`);
          if (isSending === false) {
            return;
          }
        }
      } else {
        // Add message to existing conversation
        response = await addMessageToConversation(
          conversationId,
          messageContent
        );
        if (!response) return; // Request was aborted
      }

      updateMessagesWithResponse(
        tempUserMessage,
        response.id,
        response.reply,
        response.references ?? []
      );
      scrollToBottom();
    } catch (error) {
      // Remove temp message on error
      setMessages((prev: Message[]) =>
        prev.filter((msg) => msg.id !== tempUserMessage.id)
      );
      handleAxiosAndCaughtError(error);
    } finally {
      setIsSending(false);
    }
  };

  const scrollToBottom = useCallback(() => {
    messagesContainerRef.current?.scrollTo({
      top: messagesContainerRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messagesContainerRef]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.target as HTMLDivElement;
      const { scrollTop, scrollHeight, clientHeight } = target;

      // Detect if user is at the bottom
      const atBottom = scrollTop + clientHeight >= scrollHeight - 10;
      setIsAtBottom(atBottom);

      if (scrollTop < 100 && hasMoreOlder && !isLoadingOlder && !olderError) {
        loadOlderMessages();
      }

      if (
        scrollTop + clientHeight > scrollHeight - 100 &&
        hasMoreNewer &&
        !isLoadingNewer &&
        !newerError
      ) {
        loadNewerMessages();
      }
    },
    [
      hasMoreOlder,
      hasMoreNewer,
      isLoadingOlder,
      isLoadingNewer,
      olderError,
      newerError,
      loadOlderMessages,
      loadNewerMessages,
    ]
  );

  const retryLoadInitialMessages = () => {
    setLoadError(null);
    loadInitialMessages();
  };

  const retryLoadOlderMessages = () => {
    setOlderError(null);
    loadOlderMessages();
  };

  const retryLoadNewerMessages = () => {
    setNewerError(null);
    loadNewerMessages();
  };

  useEffect(() => {
    if (conversationId) {
      loadInitialMessages();
    }
  }, [conversationId, loadInitialMessages]);

  return {
    sendMessage,
    scrollToBottom,
    handleScroll,
    retryLoadInitialMessages,
    retryLoadOlderMessages,
    retryLoadNewerMessages,
  };
}
